import { useState } from "react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  CalendarClock, Swords, MapPin, Users, Search,
  Star, Skull, Flag, Sparkles, Filter,
} from "lucide-react";

interface TimelineEvent {
  title: string;
  description: string;
  category: string;
  timestamp_label: string;
  turn?: number;
  importance?: number;
  linked_entities?: string[];
}

interface TimelineWidgetProps {
  events?: TimelineEvent[] | null;
  timeFormat?: string;
  calendarEpoch?: string;
  compact?: boolean;
}

const CATEGORY_META: Record<string, { icon: React.ComponentType<any>; color: string; label: string }> = {
  discovery: { icon: Search, color: "text-amber-400", label: "Discovery" },
  combat: { icon: Swords, color: "text-red-400", label: "Combat" },
  travel: { icon: MapPin, color: "text-blue-400", label: "Travel" },
  social: { icon: Users, color: "text-green-400", label: "Social" },
  quest: { icon: Flag, color: "text-purple-400", label: "Quest" },
  death: { icon: Skull, color: "text-destructive", label: "Death" },
  milestone: { icon: Star, color: "text-primary", label: "Milestone" },
  custom: { icon: Sparkles, color: "text-muted-foreground", label: "Event" },
};

function getCategoryMeta(category: string) {
  return CATEGORY_META[category] || CATEGORY_META.custom;
}

export function TimelineWidget({ events, timeFormat, calendarEpoch, compact }: TimelineWidgetProps) {
  const [filter, setFilter] = useState<string | null>(null);
  const allEvents = events || [];
  const filtered = filter ? allEvents.filter((e) => e.category === filter) : allEvents;
  // Show newest first
  const sorted = [...filtered].reverse();

  if (compact) {
    const count = allEvents.length;
    const last = allEvents[allEvents.length - 1];
    if (!count) return null;
    return (
      <div className="flex items-center gap-1 text-muted-foreground">
        <CalendarClock className="w-3 h-3" />
        <span className="text-[10px]">{count} events</span>
        {last && <span className="text-[10px] opacity-60 truncate max-w-[80px]">· {last.title}</span>}
      </div>
    );
  }

  if (allEvents.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-10 gap-2 text-muted-foreground/40">
        <CalendarClock className="w-10 h-10" />
        <p className="text-xs">No events recorded yet</p>
        <p className="text-[10px] text-muted-foreground/30">Events will appear here as your story unfolds</p>
      </div>
    );
  }

  const categories = [...new Set(allEvents.map((e) => e.category))];

  return (
    <div className="space-y-3">
      {/* Category filter chips */}
      {categories.length > 1 && (
        <div className="flex items-center gap-1.5 flex-wrap">
          <Filter className="w-3 h-3 text-muted-foreground/50" />
          <button
            onClick={() => setFilter(null)}
            className={cn(
              "text-[10px] px-2 py-0.5 rounded-full border transition-colors",
              !filter ? "bg-primary/15 border-primary/30 text-primary" : "border-border/50 text-muted-foreground hover:text-foreground"
            )}
          >
            All ({allEvents.length})
          </button>
          {categories.map((cat) => {
            const meta = getCategoryMeta(cat);
            const count = allEvents.filter((e) => e.category === cat).length;
            return (
              <button
                key={cat}
                onClick={() => setFilter(filter === cat ? null : cat)}
                className={cn(
                  "text-[10px] px-2 py-0.5 rounded-full border transition-colors",
                  filter === cat ? "bg-primary/15 border-primary/30 text-primary" : "border-border/50 text-muted-foreground hover:text-foreground"
                )}
              >
                {meta.label} ({count})
              </button>
            );
          })}
        </div>
      )}

      {/* Timeline */}
      <ScrollArea className="max-h-[400px]">
        <div className="relative pl-5">
          {/* Vertical line */}
          <div className="absolute left-[7px] top-2 bottom-2 w-px bg-border/50" />

          <div className="space-y-3">
            {sorted.map((event, i) => {
              const meta = getCategoryMeta(event.category);
              const Icon = meta.icon;
              const importance = event.importance || 5;

              return (
                <div key={`${event.title}-${i}`} className="relative group">
                  {/* Dot on the line */}
                  <div className={cn(
                    "absolute -left-5 top-1.5 w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center",
                    importance >= 8
                      ? "border-primary bg-primary/20"
                      : importance >= 5
                        ? "border-border bg-muted/50"
                        : "border-border/50 bg-background"
                  )}>
                    <div className={cn(
                      "w-1.5 h-1.5 rounded-full",
                      importance >= 8 ? "bg-primary" : "bg-muted-foreground/40"
                    )} />
                  </div>

                  <div className={cn(
                    "rounded-lg p-2.5 transition-colors border",
                    importance >= 8
                      ? "bg-primary/5 border-primary/20"
                      : "bg-muted/10 border-border/30 hover:bg-muted/20"
                  )}>
                    {/* Header */}
                    <div className="flex items-start gap-1.5 mb-1">
                      <Icon className={cn("w-3.5 h-3.5 mt-0.5 shrink-0", meta.color)} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-xs font-medium text-foreground">{event.title}</span>
                          {importance >= 8 && (
                            <Star className="w-2.5 h-2.5 text-primary fill-primary" />
                          )}
                        </div>
                        <div className="text-[10px] text-muted-foreground/60 mt-0.5">
                          {event.timestamp_label}
                        </div>
                      </div>
                    </div>

                    {/* Description */}
                    <p className="text-[11px] text-muted-foreground leading-relaxed pl-5">
                      {event.description}
                    </p>

                    {/* Linked entities */}
                    {event.linked_entities && event.linked_entities.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1.5 pl-5">
                        {event.linked_entities.map((entity) => (
                          <Badge key={entity} variant="outline" className="text-[9px] h-4 px-1.5">
                            {entity}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </ScrollArea>

      {/* Footer */}
      <div className="text-[10px] text-muted-foreground/40 text-center">
        {filtered.length} of {allEvents.length} events
        {timeFormat === "calendar" && calendarEpoch && (
          <span> · Started: {calendarEpoch}</span>
        )}
      </div>
    </div>
  );
}

/** Compact status bar version */
export function TimelineStatus({ eventCount, lastEvent, compact }: { eventCount?: number; lastEvent?: string; compact?: boolean }) {
  if (!eventCount) return null;
  return (
    <div className="flex items-center gap-1 text-muted-foreground">
      <CalendarClock className="w-3 h-3" />
      <span className="text-[10px]">{eventCount}</span>
      {lastEvent && !compact && (
        <span className="text-[10px] opacity-60 truncate max-w-[80px]">· {lastEvent}</span>
      )}
    </div>
  );
}
